import type { z as Z } from 'zod';
import { z } from 'zod';
import { date, enums, general, string } from './zodErrorMessages';

// Custom error map for zod enums
export const enumErrorMap = (issue: Z.ZodIssueOptionalMessage, field: string) => {
	switch (issue.code) {
		case 'invalid_type':
			return { message: `${enums.invalid_type} ${field}` };
		case 'invalid_enum_value':
			return { message: enums.invalid_enum_value };
		default:
			return { message: enums.default };
	}
};
// Custom error map for zod dates
export const dateErrorMap = (issue: Z.ZodIssueOptionalMessage) => {
	switch (issue.code) {
		default:
			return { message: date.default };
	}
};

/**
 * Given a form field returns its equivalent value to use with Zod.
 *
 * Ex.:
 *
 * 		0 -> undefined
 *
 * 		"" -> undefined
 *
 * 		"12" -> 12
 *
 * 		"valid text" -> "valid text"
 * @param arg A form data value
 * @returns The parsed value
 */
export const formatValue = (arg: string): string | number | undefined => {
	// If the arg is an empty string or 0 return undefined
	if (!arg) return undefined;

	// If the arg is a valid text return it
	if (isNaN(Number(arg))) return arg;

	// If the arg can be a valid number return it as a number
	return Number(arg);
};

/**
 * Given a form data return a object with the fields formatted to be able to be processed by Zod.
 *
 * Ex.:
 *
 * Original:
 *
 * 			{ "hero": "Asimov", "age": "27", "hobby": "" }
 *
 * Parsed one:
 *
 * 			{ hero: "Asimov", age: 27, hobby: undefined }
 *
 * @param formData A set of key/value pairs representing form fields and their values
 * @param avoidParseValue An array of values that we don't want to process with formatValue()
 * @returns A object parsed with the form data
 */
export const parseFormData = (formData: FormData, avoidParseValue: string[] = [], avoidAll = false) => {
	// Get the key/value object from the form data
	const rawFormData = Object.fromEntries(formData);

	// { [x: string]: string | number | undefined } = {}
	const parsedData: Record<string, unknown> = {};

	// Get only the keys
	const keys = Object.keys(rawFormData);

	for (let index = 0; index < keys.length; index++) {
		if (avoidAll || avoidParseValue.find((d) => d === keys[index])) {
			// Update the object copied
			parsedData[keys[index]] = rawFormData[keys[index]];
		} else {
			// Update the object copied with his parsed value
			parsedData[keys[index]] = formatValue(rawFormData[keys[index]] as never);
		}
	}

	return parsedData;
};

/**
 * Given an Object of array of zod errors return it as a object of only strings.
 *
 * Ex.:
 *
 * Original:
 *
 * 			{ notes: [ "error 1", "error 2" ] }
 *
 * Parsed one:
 *
 * 			{ notes:  "error 1, error 2" }
 *
 * @param formErrors The key value object
 * @returns A object parsed with the form data
 */
export const parseFormErrors = (formErrors: { [k: string]: string[] | undefined }) => {
	const parsedErrors: { [x: string]: undefined | string } = {};

	// Get only the keys
	const keys = Object.keys(formErrors);

	for (let index = 0; index < keys.length; index++) {
		// Update the object copied with his parsed value
		parsedErrors[keys[index]] = formErrors[keys[index]]?.join(' | ');
	}

	return parsedErrors;
};

export const ProductNameSchema = z.object({
	product_name: z
		.string({ required_error: general.field_required })
		.min(3, { message: string.min[3] })
		.max(40, { message: string.max[40] })
		.trim()
});
export const testProductName = (productName: { product_name: string } | Record<string, unknown>) => {
	const result = ProductNameSchema.safeParse(productName);
	if (!result.success) return result.error.flatten().fieldErrors;
};
